import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Group,
  NavLink,
  Popover,
  Skeleton,
  Text,
  useMatches
} from "@mantine/core";
import { IconHelp, IconSettings } from "@tabler/icons-react";
import { Link } from "react-router";
import { PUBLIC_ROUTES, ROUTES } from "../../routes";
import type { CurrentUser } from "../../utils/backend-types";
import { SiteName } from "../../utils/enums";
import { DefaultUserPic } from "../DefaultUserPic/DefaultUserPic";
import { logout } from "../Login/utils/logout";
import "./UserMenu.scss";

declare const BACKEND_BASE_URL: string | undefined;

const BASE_URL = BACKEND_BASE_URL || "";

const SECONDARY_LINKS: ReadonlyArray<{
  url: string;
  label: string;
  siteNames: ReadonlyArray<SiteName>;
}> = [
  {
    url: `${BASE_URL}/seiten/datenschutz`,
    label: "Datenschutz",
    siteNames: [SiteName.Smarendo]
  },
  {
    url: "https://www.alva-energie.de/datenschutz",
    label: "Datenschutz",
    siteNames: [SiteName.Alva]
  },
  {
    url: "https://www.node.energy/legal/avv",
    label: "Datenschutz",
    siteNames: [SiteName.Optinode]
  },
  {
    url: `${BASE_URL}/seiten/SBOM`,
    label: "SBOM",
    siteNames: [SiteName.Smarendo, SiteName.Optinode, SiteName.Alva]
  }
] as const;

function onLogout(e: React.MouseEvent) {
  e.preventDefault();
  logout();
}

interface UserMenuProps {
  currentUser: CurrentUser | undefined;
  siteName: SiteName;
}

function UserMenu({ currentUser, siteName }: UserMenuProps) {
  const secondaryLinksForCurrentSite = SECONDARY_LINKS.filter(({ siteNames }) =>
    siteNames.includes(siteName)
  );

  return (
    <Center className="UserMenu" h="100%" mr={30}>
      <Popover arrowSize={20} position="bottom" withArrow>
        <Popover.Target>
          <Button
            aria-label="Usermenu öffnen"
            h="100%"
            p={0}
            variant="transparent"
          >
            <UserAvatar currentUser={currentUser} />
          </Button>
        </Popover.Target>
        <Popover.Dropdown className="user-menu-dropdown" p={0}>
          <Card miw={325} radius="sm" shadow="sm">
            <Card.Section bg="primary" mb="lg" p="lg">
              <Group wrap="nowrap">
                <UserAvatar currentUser={currentUser} />
                {currentUser ? (
                  <div>
                    <Text
                      c="white"
                      component={Link}
                      fw={400}
                      size="xl"
                      to={ROUTES.settings}
                    >
                      {currentUser.name}
                    </Text>
                    <Text c="white" fw={300}>
                      {currentUser.email}
                    </Text>
                    {currentUser.is_beta_user && (
                      <Badge color="warning" size="sm">
                        Beta-User
                      </Badge>
                    )}
                  </div>
                ) : (
                  <Box flex="1 1 auto">
                    <Skeleton height={25} mb="xs" />
                    <Skeleton height={19} />
                  </Box>
                )}
              </Group>
            </Card.Section>
            <NavLink
              component={Link}
              label="Einstellungen"
              leftSection={<IconSettings size="24" stroke={1} />}
              px={0}
              to={ROUTES.settings}
            />
            {siteName !== SiteName.Alva && (
              <NavLink
                component={Link}
                label="Support"
                leftSection={<IconHelp size="24" stroke={1} />}
                px={0}
                to={PUBLIC_ROUTES.support}
              />
            )}
            <Button
              color="primary"
              mt="lg"
              variant="outline"
              onClick={onLogout}
            >
              Logout
            </Button>
            {secondaryLinksForCurrentSite.length > 0 && (
              <Group gap="md" justify="flex-end" mt="xl">
                {secondaryLinksForCurrentSite.map(({ url, label }) => (
                  <div key={label}>
                    <a href={url}>{label}</a>
                  </div>
                ))}
              </Group>
            )}
          </Card>
        </Popover.Dropdown>
      </Popover>
    </Center>
  );
}

function UserAvatar({ currentUser }: { currentUser: CurrentUser | undefined }) {
  const size = useMatches({
    base: "sm",
    md: "4rem"
  });
  return currentUser ? (
    <>
      <Avatar
        alt={`Profilbild von ${currentUser.name}`}
        name={currentUser.name}
        size={size}
        src={currentUser.image}
      >
        <img alt="Platzhalter für Profilbild" src={DefaultUserPic} />
      </Avatar>
    </>
  ) : (
    <Skeleton circle height="4rem" />
  );
}

export { UserMenu, UserMenuProps };
